/**
 * Arriving in a world: you stand where the rules say players arrive (the hub's tile, or where it
 * would stand while there is none). The planet turns so that tile is on top and what you came for
 * lies straight ahead of you, where the camera looks over your shoulder (-z): the nearest crystal
 * deposit (where a first machine goes), or else the nearest tree.
 */
import { arrivalTile, gridOf, KIND, stepsFrom, type CrewState } from '@bramvasterd/terrella-rules';

import {
  quat,
  quatApply,
  quatFromAxisAngle,
  quatFromUnitVectors,
  quatPremultiply,
  UP,
  vec,
  vecSet,
} from '@core/quat';

import type { Local } from './local';

const center = vec();
const ahead = vec();
const spin = quat();

/** The tile to face from `from`: the nearest ore by steps, else the nearest tree; null for neither. */
export function landmarkOf(state: CrewState, from: number): number | null {
  const steps = stepsFrom(gridOf(state), [from]);
  let best: number | null = null;
  let rank = Infinity;
  for (let i = 0; i < steps.length; i++) {
    const s = steps[i] ?? -1;
    if (s <= 0) continue;
    const kind = state.kind[i];
    const r = kind === KIND.ORE ? s : kind === KIND.TREE ? steps.length + s : Infinity;
    if (r < rank) {
      rank = r;
      best = i;
    }
  }
  return best;
}

/** Turns the planet so I stand where players arrive, facing the nearest crystal deposit or tree. */
export function standOnArrival(local: Local, state: CrewState): void {
  const arrival = arrivalTile(state);
  const grid = gridOf(state);
  center.set(grid.centers.subarray(arrival * 3, arrival * 3 + 3));
  quatFromUnitVectors(local.q, center, UP);
  const landmark = landmarkOf(state, arrival);
  if (landmark !== null) {
    ahead.set(grid.centers.subarray(landmark * 3, landmark * 3 + 3));
    quatApply(ahead, local.q, ahead);
    // a turn about the up axis that brings it round to -z
    quatFromAxisAngle(spin, 0, 1, 0, Math.atan2(ahead[0] ?? 0, -(ahead[2] ?? 0)));
    quatPremultiply(local.q, spin);
  }
  vecSet(local.heading, 0, 0, -1);
  local.walk = null;
}
